import type {
  Link,
  CreateLinkRequest,
  UpdateLinkRequest,
  LinksListRequest,
  LinksListResponse,
  ClickLogRequest,
  QRCodeRequest,
  StatsResponse,
} from '@/types/api'

const KERNEL_API_BASE_URL = (import.meta as unknown as { env?: Record<string, unknown> })
  .env?.VITE_KERNEL_API_BASE_URL as string | undefined
const KERNEL_API_KEY = (import.meta as unknown as { env?: Record<string, unknown> })
  .env?.VITE_KERNEL_API_KEY as string | undefined

const ADMIN_TOKEN_KEY = 'blink_admin_token'
const ADMIN_TOKEN_EXPIRES_AT_KEY = 'blink_admin_token_expires_at'

if (!KERNEL_API_BASE_URL) {
  console.warn('[kernelBlinkClient] VITE_KERNEL_API_BASE_URL is not set')
}

if (!KERNEL_API_KEY) {
  console.warn('[kernelBlinkClient] VITE_KERNEL_API_KEY is not set')
}

export interface AdminVerifyResponse {
  authenticated: boolean
  token?: string
  expiresAt?: string
}

export function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(ADMIN_TOKEN_KEY)
}

export function setAdminSession(token: string, expiresAt?: string) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(ADMIN_TOKEN_KEY, token)
  if (expiresAt) {
    window.localStorage.setItem(ADMIN_TOKEN_EXPIRES_AT_KEY, expiresAt)
  }
}

export function clearAdminSession() {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(ADMIN_TOKEN_KEY)
  window.localStorage.removeItem(ADMIN_TOKEN_EXPIRES_AT_KEY)
}

async function kernelBlinkFetch(path: string, options: RequestInit = {}) {
  const baseUrl = KERNEL_API_BASE_URL || ''
  const apiKey = KERNEL_API_KEY || ''

  const url = `${baseUrl.replace(/\/+$/, '')}/blink${path}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
  }

  const adminToken = getAdminToken()
  if (adminToken) {
    headers['x-admin-token'] = adminToken
  }

  if (options.headers) {
    const incoming = options.headers as Record<string, string>
    for (const key in incoming) {
      headers[key] = String(incoming[key])
    }
  }

  const res = await fetch(url, { ...options, headers })

  if (!res.ok) {
    throw new Error(`Kernel error ${res.status}`)
  }

  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return res.json()
  }

  return res
}

export async function verifyAdmin(password: string): Promise<AdminVerifyResponse> {
  const baseUrl = KERNEL_API_BASE_URL || ''
  const apiKey = KERNEL_API_KEY || ''

  const url = `${baseUrl.replace(/\/+$/, '')}/admin/verify`

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'x-app-id': 'blink',
    },
    body: JSON.stringify({
      appId: 'blink',
      password,
    }),
  })

  if (!res.ok) {
    throw new Error(`Kernel error ${res.status}`)
  }

  const result = (await res.json()) as AdminVerifyResponse

  if (result.authenticated && result.token) {
    setAdminSession(result.token, result.expiresAt)
  }

  return result
}

function buildSearchParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

export async function listLinks(params: LinksListRequest): Promise<LinksListResponse> {
  const query = buildSearchParams({
    query: params.query,
    page: params.page,
    pageSize: params.pageSize,
    active: params.active,
  })

  return kernelBlinkFetch(`/links${query}`)
}

export async function createLink(payload: CreateLinkRequest): Promise<Link> {
  return kernelBlinkFetch('/links', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateLink(payload: UpdateLinkRequest): Promise<Link> {
  return kernelBlinkFetch('/links', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function deleteLink(id: string): Promise<void> {
  await kernelBlinkFetch(`/links?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function getLinkByCode(code: string): Promise<Link> {
  return kernelBlinkFetch(`/links/by-code?code=${encodeURIComponent(code)}`)
}

export async function getPublicLink(code: string): Promise<Link> {
  return kernelBlinkFetch(`/public-link?code=${encodeURIComponent(code)}`)
}

export async function getStats(): Promise<StatsResponse> {
  return kernelBlinkFetch('/stats/summary')
}

export async function logClick(payload: ClickLogRequest): Promise<void> {
  await kernelBlinkFetch('/clicks-log', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export interface BulkUploadItem {
  targetUrl: string
  code?: string
  title?: string
  expiresAt?: string
}

export interface BulkUploadRequest {
  csvData: BulkUploadItem[]
}

export interface BulkUploadResultItem {
  code: string
  targetUrl: string
  success: boolean
  error?: string
}

export interface BulkUploadResponse {
  results: BulkUploadResultItem[]
}

export async function bulkUpload(payload: BulkUploadRequest): Promise<BulkUploadResponse> {
  return kernelBlinkFetch('/links/bulk-upload', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getQrCode(params: QRCodeRequest): Promise<Blob> {
  const query = buildSearchParams({
    code: params.code,
    format: params.format,
    size: params.size,
  })

  const res = (await kernelBlinkFetch(`/links/qrcode${query}`)) as Response

  const format = params.format || 'svg'

  if (format === 'svg') {
    const svgText = await res.text()
    return new Blob([svgText], { type: 'image/svg+xml' })
  }

  return res.blob()
}

export const kernelBlinkClient = {
  verifyAdmin,
  listLinks,
  createLink,
  updateLink,
  deleteLink,
  getLinkByCode,
  getPublicLink,
  getStats,
  logClick,
  bulkUpload,
  getQrCode,
}

export { kernelBlinkFetch }

